import { Route, Routes } from 'react-router-dom';
import { RequireAuth } from './auth/RequireAuth.tsx';
import { ToastProvider } from './components/Toast.tsx';
import { SessionProvider } from './hooks/useSession.tsx';
import { Shell } from './layout/Shell.tsx';
import { Billing } from './pages/Billing.tsx';
import { BillingTopupPage } from './pages/BillingTopupPage.tsx';
import { BillingTopupReturnPage } from './pages/BillingTopupReturnPage.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { Invite } from './pages/Invite.tsx';
import { Keys } from './pages/Keys.tsx';
import { Login } from './pages/Login.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { Pricing } from './pages/Pricing.tsx';
import { Register } from './pages/Register.tsx';
import { Requests } from './pages/Requests.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { RootRedirect } from './pages/RootRedirect.tsx';
import { Settings } from './pages/Settings.tsx';
import { Usage } from './pages/Usage.tsx';
import { VerifyEmail } from './pages/VerifyEmail.tsx';

/**
 * Top-level tenant-console route table.
 *
 * Unauthenticated routes render bare cards on a solid backdrop
 * (`Login`, `Register`, `VerifyEmail`, `ForgotPassword`,
 * `ResetPassword`). Authenticated routes live inside a nested
 * `<RequireAuth><Shell>` tree.
 *
 * The QueryClientProvider lives in `main.tsx` so tests can mount a
 * fresh client per render without re-creating the whole tree.
 */
export function App(): JSX.Element {
  return (
    <SessionProvider>
      <ToastProvider>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<Invite />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Shell>
                  <Routes>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/keys" element={<Keys />} />
                    <Route path="/usage" element={<Usage />} />
                    <Route path="/requests" element={<Requests />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/billing/topup" element={<BillingTopupPage />} />
                    <Route path="/billing/topup/return" element={<BillingTopupReturnPage />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Shell>
              </RequireAuth>
            }
          />
        </Routes>
      </ToastProvider>
    </SessionProvider>
  );
}
